import React, { useState, useEffect, useCallback } from 'react';
import { getLineasCreditoPaginadas, darDeBajaLineaCredito } from '../services/lineaCreditoService';
import { getTipoCreditoById } from '../services/tipoCreditoService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, RefreshCw, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, X, Landmark, Calculator, Percent, DollarSign, Info, ShieldCheck, Database, Layers } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

import LineaCreditoForm from './LineaCreditoForm';

export default function LineasCreditoList() {
    const [lineas, setLineas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();
    const { token } = useAuth();

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const cargarLineas = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getLineasCreditoPaginadas(token, page, pageSize, debouncedSearch);
            setLineas(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setError(null);
        } catch (error) {
            console.error('Error al cargar:', error);
            setError('Error de conexión');
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    }, [token, page, pageSize, debouncedSearch]);

    useEffect(() => {
        cargarLineas();
    }, [cargarLineas]);

    const handleEdit = (id) => {
        setEditingId(id);
        setIsModalOpen(true);
    };

    const handleNew = () => {
        setEditingId(null);
        setIsModalOpen(true);
    };

    const handleDarDeBaja = async (id) => {
        if (window.confirm('¿Desactivar esta línea comercial?')) {
            try {
                await darDeBajaLineaCredito(token, id);
                toast.success('Producto desactivado');
                cargarLineas();
            } catch (error) {
                toast.error('No se pudo desactivar');
            }
        }
    };

    const startItem = totalElements === 0 ? 0 : page * pageSize + 1;
    const endItem = Math.min((page + 1) * pageSize, totalElements);

    return (
        <div className="max-w-[98%] mx-auto bg-white shadow-sm border border-gray-200 mb-10 overflow-hidden rounded-sm">
            <Toaster position="top-right" />

            {/* Header Enterprise Style (Slate) */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion#financieros')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-lg font-semibold tracking-tight uppercase">Mantenimiento: Líneas de Crédito (Product Factory)</h1>
                    </div>
                </div>

                <button
                    onClick={handleNew}
                    className="flex items-center gap-1.5 transition-all rounded-sm px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm text-[0.85rem] font-bold uppercase"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVA LÍNEA
                </button>
            </div>

            {/* Controles Compactos */}
            <div className="p-3 bg-gray-50 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    <div className="relative group">
                        <input
                            type="text"
                            placeholder="Buscar producto o cuenta..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-white border border-gray-300 pl-8 pr-3 py-1.5 w-64 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm shadow-sm"
                        />
                        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5 group-focus-within:text-blue-500" />
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-gray-500 uppercase">Mostrar:</label>
                        <select
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                            className="bg-white border border-gray-300 px-2 py-1 text-[0.875rem] font-medium outline-none rounded-sm shadow-sm"
                            disabled={loading}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                </div>

                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    {loading && <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />}
                    VISTA EXTENDIDA DE AUDITORÍA
                </div>
            </div>

            {/* Grilla Principal con Scroll Horizontal */}
            <div className="overflow-x-auto relative">
                <table className="min-w-full text-left border-collapse table-fixed">
                    <thead>
                        <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-[10px] font-bold uppercase tracking-wider">
                            <th className="px-3 py-2 w-12 text-center border-r border-gray-200">ID</th>
                            <th className="px-3 py-2 w-56 border-r border-gray-200">Nombre de Línea / Producto</th>
                            <th className="px-3 py-2 w-32 border-r border-gray-200 text-right">Monto Mín.</th>
                            <th className="px-3 py-2 w-32 border-r border-gray-200 text-right">Monto Máx.</th>
                            <th className="px-3 py-2 w-20 border-r border-gray-200 text-center">Tasa %</th>
                            <th className="px-3 py-2 w-32 border-r border-gray-200">Categoría (Tipo)</th>
                            <th className="px-3 py-2 w-32 border-r border-gray-200 text-center">Cta. Cap. Cód.</th>
                            <th className="px-3 py-2 w-48 border-r border-gray-200">Cta. Cap. Nombre</th>
                            <th className="px-3 py-2 w-32 border-r border-gray-200 text-center">Cta. Int. Cód.</th>
                            <th className="px-3 py-2 w-48 border-r border-gray-200">Cta. Int. Nombre</th>
                            <th className="px-3 py-2 w-32 text-center sticky right-0 bg-[#f8fafc] shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] border-l border-gray-200">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="text-[12px] text-gray-600">
                        {loading && lineas.length === 0 ? (
                            <tr><td colSpan="11" className="px-4 py-8 text-center opacity-50 font-bold uppercase italic border-b border-gray-200">Sincronizando grilla extendida...</td></tr>
                        ) : lineas.length === 0 ? (
                            <tr><td colSpan="11" className="px-4 py-8 text-center text-gray-400 font-bold uppercase italic border-b border-gray-200">No hay datos para la consulta.</td></tr>
                        ) : (
                            lineas.map((linea) => (
                                <tr
                                    key={linea.id}
                                    className="border-b border-gray-100 hover:bg-[#f1f5f9] transition-colors"
                                >
                                    <td className="px-3 py-1.5 text-center font-mono text-[11px] text-gray-400 border-r border-gray-100">{linea.id}</td>
                                    <td className="px-3 py-1.5 font-bold text-gray-800 uppercase border-r border-gray-100 truncate" title={linea.nombre}>
                                        {linea.nombre}
                                        {linea.codigo && <div className="text-[9px] text-blue-500 font-mono tracking-tighter">REF: {linea.codigo}</div>}
                                    </td>
                                    <td className="px-3 py-1.5 text-right font-medium border-r border-gray-100">{linea.montoMinimo?.toLocaleString()}</td>
                                    <td className="px-3 py-1.5 text-right font-medium border-r border-gray-100">{linea.montoMaximo?.toLocaleString()}</td>
                                    <td className="px-3 py-1.5 text-center font-bold text-blue-600 border-r border-gray-100">{linea.tasaNominal}%</td>
                                    <td className="px-3 py-1.5 border-r border-gray-100 uppercase text-[11px] text-slate-500 font-bold">{linea.tipoCreditoNombre}</td>
                                    <td className="px-3 py-1.5 text-center font-mono text-emerald-700 border-r border-gray-100">{linea.cuentaCapitalCodigo || '---'}</td>
                                    <td className="px-3 py-1.5 border-r border-gray-100 truncate text-[11px]" title={linea.cuentaCapitalNombre}>{linea.cuentaCapitalNombre || '---'}</td>
                                    <td className="px-3 py-1.5 text-center font-mono text-orange-700 border-r border-gray-100">{linea.cuentaInteresCodigo || '---'}</td>
                                    <td className="px-3 py-1.5 border-r border-gray-100 truncate text-[11px]" title={linea.cuentaInteresNombre}>{linea.cuentaInteresNombre || '---'}</td>
                                    <td className="px-3 py-1.5 text-center sticky right-0 bg-white shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] border-l border-gray-200 group-hover:bg-[#f1f5f9]">
                                        <div className="flex justify-center gap-3">
                                            <button
                                                onClick={() => handleEdit(linea.id)}
                                                className="text-blue-600 hover:text-blue-800 font-bold text-[10px] uppercase tracking-tighter"
                                            >
                                                EDITAR
                                            </button>
                                            {linea.activo && (
                                                <button
                                                    onClick={() => handleDarDeBaja(linea.id)}
                                                    className="text-red-500 hover:text-red-700 font-bold text-[10px] uppercase tracking-tighter"
                                                >
                                                    ELIMINAR
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación Enterprise */}
            <div className="p-3 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-2 border-t border-gray-200 text-[0.875rem] text-gray-500">
                <div>
                    Mostrando <span className="font-bold text-gray-700">{startItem} a {endItem}</span> de <span className="font-bold text-gray-700">{totalElements}</span> registros
                </div>

                <div className="flex items-center gap-1">
                    <button onClick={() => setPage(0)} disabled={page === 0 || loading} className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm italic text-xs">P Primero</button>
                    <button onClick={() => setPage(page - 1)} disabled={page === 0 || loading} className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm"><ChevronLeft className="w-4 h-4" /></button>

                    <div className="px-4 py-1 border border-blue-600 bg-blue-600 text-white font-bold text-xs rounded-sm">
                        {page + 1}
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 px-1 uppercase">de {totalPages || 1}</span>

                    <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1 || loading} className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm"><ChevronRight className="w-4 h-4" /></button>
                    <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1 || totalPages === 0 || loading} className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-30 rounded-sm italic text-xs">U Último</button>
                </div>
            </div>

            {/* MODAL INTEGRADO */}
            <LineaCreditoForm
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                id={editingId}
                onSaveSuccess={cargarLineas}
            />
        </div>
    );
}
